import React from "react"
import { Box, IconButton, Menu, MenuItem, Badge, useTheme, alpha, Typography } from "@mui/material"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { useRouter } from "next/router"
import { useSession } from "next-auth/react"
import { signOut } from "next-auth/react"
import { useTranslation } from "@/contexts/LanguageContext"
import { APP_MENU_ITEMS } from "@/constants/menuItems"
import { useUserPreferences } from "@/hooks/useUserPreferences"
import { isSessionAdmin } from "@/utils/auth"

export function MobileBottomNav() {
  const theme = useTheme()
  const router = useRouter()
  const { data: session } = useSession()
  const { t } = useTranslation()
  const { preferences } = useUserPreferences()
  const isAdmin = isSessionAdmin(session)
  const [moreMenuAnchor, setMoreMenuAnchor] = React.useState<null | HTMLElement>(null)

  const hiddenMenuItems = React.useMemo(() => new Set(preferences.hiddenMenuItems || []), [preferences.hiddenMenuItems])

  const menuItems = React.useMemo(() => {
    const roles = Array.isArray(session?.user?.roles) ? session.user.roles : []
    const rolesSet = new Set(roles)
    const hasCustomRoles = rolesSet.size > 0

    // Filter out admin-only items (they'll be shown in the user menu instead)
    // Also filter out user-settings (it's only accessible from the header user menu)
    const adminOnlyIds = new Set([
      "settings",
      "docs",
      "user-manual",
      "user-access",
      "automation",
      "user-settings",
      "job-monitoring",
      "job-toggles",
      "alfredo-settings",
      "alfredo-import-manager",
      "pipeline-diagnostics",
      "hot-deals-zones",
      "favorites",
      "disliked"
    ])

    return APP_MENU_ITEMS.filter((item) => {
      // Exclude admin-only items from bottom nav
      if (adminOnlyIds.has(item.id)) {
        return false
      }
      // Exclude globally-hidden items and user-hidden items
      if (item.hidden || hiddenMenuItems.has(item.id)) {
        return false
      }
      if (item.action === "signout") {
        return false // Sign out is in user menu
      }
      if (isAdmin) {
        return true
      }
      if (rolesSet.has(item.id)) {
        return true
      }
      if (!hasCustomRoles && !item.adminOnly) {
        return true
      }
      return false
    })
  }, [isAdmin, session, hiddenMenuItems])

  const visibleItems = menuItems.slice(0, 4)
  const moreItems = menuItems.slice(4)

  const handleMenuItemClick = (item: (typeof APP_MENU_ITEMS)[0]) => {
    if (item.action === "signout") {
      signOut()
      return
    }

    if (item.href) {
      window.open(item.href, "_blank")
      return
    }

    if (item.route !== undefined) {
      if (item.route.startsWith("http")) {
        window.open(item.route, "_blank")
      } else if (item.route && item.route.length > 0) {
        router.push(`/features/${item.route}`)
      } else {
        // Empty route means dashboard - go to /features
        router.push("/features")
      }
    } else {
      // No route defined, default to dashboard
      router.push("/features")
    }

    // Close more menu if open
    setMoreMenuAnchor(null)
  }

  const isActive = (item: (typeof APP_MENU_ITEMS)[0]) => {
    if (item.route) {
      const currentPath = router.asPath
      if (item.route.length === 0) {
        return currentPath === "/features" || currentPath === "/" || currentPath === "/#"
      }
      return currentPath.startsWith(`/features/${item.route}`)
    }
    return false
  }

  const hasActiveInMore = moreItems.some(isActive)

  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoreMenuAnchor(event.currentTarget)
  }

  const handleMoreClose = () => {
    setMoreMenuAnchor(null)
  }

  if (!session) {
    return null
  }

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          height: 64,
          backgroundColor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: { xs: "flex", md: "none" },
          alignItems: "center",
          justifyContent: "space-around",
          px: 1,
          zIndex: 1200,
          boxShadow: "0 -2px 10px rgba(0,0,0,0.1)"
        }}>
        {visibleItems.map((item) => {
          const label = t(item.labelKey)
          const IconComponent = item.icon
          const active = isActive(item)

          return (
            <IconButton
              key={item.id}
              onClick={() => handleMenuItemClick(item)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
                color: active ? theme.palette.primary.main : theme.palette.text.secondary,
                "&:hover": { backgroundColor: "transparent", color: active ? theme.palette.primary.main : theme.palette.text.primary },
                minWidth: 60,
                borderRadius: 2,
                py: 1
              }}>
              <IconComponent sx={{ fontSize: 24 }} />
              <Typography variant="caption" sx={{ fontSize: "0.65rem", fontWeight: active ? 600 : 400, color: "inherit", lineHeight: 1 }}>
                {label}
              </Typography>
            </IconButton>
          )
        })}

        {moreItems.length > 0 && (
          <IconButton
            onClick={handleMoreClick}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.25,
              color: hasActiveInMore ? theme.palette.primary.main : theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: "transparent",
                color: hasActiveInMore ? theme.palette.primary.main : theme.palette.text.primary
              },
              minWidth: 60,
              borderRadius: 2,
              py: 1
            }}>
            <Badge color="primary" variant="dot" invisible={!hasActiveInMore} sx={{ "& .MuiBadge-badge": { right: 8, top: 8 } }}>
              <MoreHorizIcon sx={{ fontSize: 24 }} />
            </Badge>
            <Typography
              variant="caption"
              sx={{ fontSize: "0.65rem", fontWeight: hasActiveInMore ? 600 : 400, color: "inherit", lineHeight: 1 }}>
              {t("navigation.more")}
            </Typography>
          </IconButton>
        )}
      </Box>

      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={handleMoreClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        PaperProps={{ sx: { minWidth: 200, maxWidth: "90vw", mb: 1 } }}>
        {moreItems.map((item) => {
          const label = t(item.labelKey)
          const IconComponent = item.icon
          const active = isActive(item)

          return (
            <MenuItem
              key={item.id}
              onClick={() => handleMenuItemClick(item)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1.5,
                backgroundColor: active ? alpha(theme.palette.primary.main, 0.08) : "transparent",
                color: active ? theme.palette.primary.main : theme.palette.text.primary,
                "&:hover": { backgroundColor: active ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.action.hover, 0.08) }
              }}>
              <IconComponent sx={{ fontSize: 20, color: "inherit" }} />
              <Typography sx={{ fontWeight: active ? 600 : 400 }}>{label}</Typography>
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}
